import React, { useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import { getMarketingRouterRuleMetrics } from '~/data/marketingRouterRuleMetrics';
import { Link } from '~/lib/react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  type MarketingRouterTreatmentType,
  getMarketingRouterTreatmentConfig,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';
import { getRegions } from '@luxuryescapes/lib-regions';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useMarketingRouterRules } from '~/hooks/apis/promo/useMarketingRouterRules';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { MarketingRouterInternalQuery } from '~/services/PromoService';
import { customerPortalHost } from '~/services/common';

import { getMarketingRouterRuleCookieName } from '~/types/marketingRouterRule';

import { isoToSydneyDate } from '~/utils/dateFormatter';

import MarketingRouterRulesFilterBar from './MarketingRouterRulesFilterBar';
import MarketingRouterTermLabel from './MarketingRouterTermLabel';
import { formatMatchValues, toMatchValues } from './marketingRouterMatch';
import {
  EMPTY_MARKETING_ROUTER_RULE_FILTERS,
  type MarketingRouterRuleStatusLabel,
  filterMarketingRouterRules,
  getMarketingRouterRuleStatus,
  hasActiveMarketingRouterRuleFilters,
  parseMarketingRouterRuleFilters,
  toMarketingRouterRuleFilterParams,
} from './marketingRouterRuleFilters';

// A link can only carry one value per parameter, so it exercises the first
// value the rule lists for each field.
function firstMatchValue(values?: Array<string>): string {
  return toMatchValues(values)[0] ?? '';
}

function getCustomerPortalTestLink(
  tenant: App.Tenant,
  rule: {
    id: string;
    match: { utm_medium?: Array<string>; utm_source?: Array<string>; utm_campaign?: Array<string> };
  },
  variantId: string,
): string {
  const query = new URLSearchParams({
    utm_medium: firstMatchValue(rule.match.utm_medium),
    utm_source: firstMatchValue(rule.match.utm_source),
    utm_campaign: firstMatchValue(rule.match.utm_campaign),
    mrid: rule.id,
    mrvid: variantId,
  });

  const portalBaseUrl = customerPortalHost(tenant)
    .replace(/\/$/, '')
    .replace(/\/[a-z]{2}$/i, '');

  return `${portalBaseUrl}/ads-traffic?${query.toString()}`;
}

function formatDate(date: string): string {
  return dayjs(date).format('DD MMM YYYY');
}

function formatAud(value: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);
}

function getSignificanceLabel(significance: 'pending' | 'not_significant' | 'significant'): string {
  if (significance === 'not_significant') {
    return 'Not significant';
  }

  return significance === 'significant' ? 'Significant' : 'Pending';
}

const STATUS_CHIP_COLOR: Record<MarketingRouterRuleStatusLabel, 'default' | 'success' | 'info'> = {
  Scheduled: 'default',
  Running: 'success',
  Ended: 'info',
};

function formatTreatments(treatments?: Array<{ type: MarketingRouterTreatmentType }>): string {
  if (!treatments?.length) {
    return '—';
  }
  return treatments
    .map((treatment) => getMarketingRouterTreatmentConfig(treatment.type)?.typeName ?? treatment.type)
    .join(', ');
}

export default function MarketingRouterRulesPage() {
  const { tenant } = useCurrentTenant();
  const query: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    page: 0,
    pageSize: 100,
  };
  const rulesQuery = useMarketingRouterRules({ query });
  const rules = rulesQuery.data ?? [];
  const sortedRules = useMemo(() => [...rules].sort((first, second) => first.id.localeCompare(second.id)), [rules]);
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);

  // Filters live in the URL so a filtered view can be shared and survives a
  // reload; the page holds no filter state of its own.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseMarketingRouterRuleFilters(searchParams), [searchParams]);
  const hasFilters = hasActiveMarketingRouterRuleFilters(filters);
  // Rule dates are Sydney calendar days, so "today" has to be one too.
  const today = useMemo(() => isoToSydneyDate(new Date().toISOString()), []);
  const shownRules = useMemo(
    () => filterMarketingRouterRules(sortedRules, filters, today),
    [sortedRules, filters, today],
  );
  const regionCodes = useMemo(
    () =>
      getRegions(tenant.brand)
        .map((region) => region.code)
        .sort(),
    [tenant.brand],
  );
  const resetFilters = () =>
    setSearchParams(toMarketingRouterRuleFilterParams(EMPTY_MARKETING_ROUTER_RULE_FILTERS), { replace: true });

  return (
    <>
      <Helmet>
        <title>Marketing / Router Rules</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title="Marketing Router Rules">
          <Button component={Link} to="/marketing/marketing-router-rules/create" variant="contained" color="primary">
            Create rule
          </Button>
        </PageHeader>
        <Card>
          <CardContent>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography variant="h6">Rules</Typography>
              <Typography color="text.secondary" variant="body2">
                Rules are managed through svc-promo for the current tenant.
              </Typography>
            </Stack>
            <MarketingRouterRulesFilterBar
              filters={filters}
              onChange={(next) => setSearchParams(toMarketingRouterRuleFilterParams(next), { replace: true })}
              onReset={resetFilters}
              regionCodes={regionCodes}
              shownCount={shownRules.length}
              totalCount={sortedRules.length}
            />
            {rulesQuery.isLoading ? <Typography color="text.secondary">Loading rules…</Typography> : null}
            {rulesQuery.isError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {rulesQuery.error instanceof Error
                  ? rulesQuery.error.message
                  : 'Failed to load marketing router rules.'}
              </Alert>
            ) : null}
            <Table aria-label="Marketing Router Rules">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <MarketingRouterTermLabel term="rule">Rule name</MarketingRouterTermLabel>
                  </TableCell>
                  <TableCell>Match</TableCell>
                  <TableCell>Persistence</TableCell>
                  <TableCell>Variants</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shownRules.map((rule) => {
                  const isExpanded = expandedRuleId === rule.id;
                  const variantsTotal = rule.variants.reduce((total, variants) => total + variants.weight, 0);
                  const status = getMarketingRouterRuleStatus(rule, today);

                  return (
                    <React.Fragment key={rule.id}>
                      <TableRow hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          <MuiLink
                            component={Link}
                            to={`/marketing/marketing-router-rules/${encodeURIComponent(rule.id)}`}
                            underline="hover"
                          >
                            {rule.name}
                          </MuiLink>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            {Object.entries(rule.match).map(([field, value]) => (
                              <Typography key={field} variant="body2">
                                <Typography component="span" variant="body2" color="text.secondary">
                                  {field.replace('utm_', 'UTM ')}:{' '}
                                </Typography>
                                {formatMatchValues(value)}
                              </Typography>
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{rule.variants.length} cookies</Typography>
                          <Typography variant="caption" color="text.secondary">
                            One per variant
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {rule.variants.length} {rule.variants.length === 1 ? 'variant' : 'variants'}
                          </Typography>
                          <Typography variant="caption" color={variantsTotal === 100 ? 'text.secondary' : 'error'}>
                            {variantsTotal}% allocated
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Chip label={status} size="small" color={STATUS_CHIP_COLOR[status]} />
                            <Typography variant="body2">{formatDate(rule.start_date)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              to {formatDate(rule.end_date)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            <IconButton
                              size="small"
                              aria-label={`${isExpanded ? 'Hide' : 'View'} variants for rule ${rule.id}`}
                              aria-expanded={isExpanded}
                              onClick={() => setExpandedRuleId(isExpanded ? null : rule.id)}
                            >
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                            <Button
                              component={Link}
                              to={`/marketing/marketing-router-rules/${encodeURIComponent(rule.id)}`}
                              variant="text"
                            >
                              Results
                            </Button>
                            <Button
                              component={Link}
                              to={`/marketing/marketing-router-rules/${rule.id}/edit?marketingRouterId=${encodeURIComponent(
                                rule.marketingRouterId,
                              )}`}
                              variant="text"
                            >
                              Edit
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0, borderBottom: isExpanded ? undefined : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Variant allocation and cookies
                              </Typography>
                              <Table size="small" aria-label={`Variants for rule ${rule.id}`}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Variant name</TableCell>
                                    <TableCell>Control</TableCell>
                                    <TableCell>
                                      <MarketingRouterTermLabel term="weight" />
                                    </TableCell>
                                    <TableCell>Treatments</TableCell>
                                    <TableCell>TTV</TableCell>
                                    <TableCell>Margin (AUD)</TableCell>
                                    <TableCell>Sessions</TableCell>
                                    <TableCell>Significance</TableCell>
                                    <TableCell>Cookie name</TableCell>
                                    <TableCell>Customer portal</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {rule.variants.map((variants) => (
                                    <TableRow key={variants.variant_id}>
                                      <TableCell>{variants.variant_name}</TableCell>
                                      <TableCell>{variants.is_control ? 'Control' : 'Test variant'}</TableCell>
                                      <TableCell>{variants.weight}%</TableCell>
                                      <TableCell>{formatTreatments(variants.treatments)}</TableCell>
                                      <TableCell>
                                        {formatAud(getMarketingRouterRuleMetrics(rule.id, variants.variant_id).ttv)}
                                      </TableCell>
                                      <TableCell>
                                        {formatAud(
                                          getMarketingRouterRuleMetrics(rule.id, variants.variant_id).margin_aud,
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {getMarketingRouterRuleMetrics(
                                          rule.id,
                                          variants.variant_id,
                                        ).sessions.toLocaleString('en-AU')}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={getSignificanceLabel(
                                            getMarketingRouterRuleMetrics(rule.id, variants.variant_id).significance,
                                          )}
                                          color={
                                            getMarketingRouterRuleMetrics(rule.id, variants.variant_id).significance ===
                                            'significant'
                                              ? 'success'
                                              : getMarketingRouterRuleMetrics(rule.id, variants.variant_id)
                                                    .significance === 'pending'
                                                ? 'warning'
                                                : 'default'
                                          }
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell sx={{ fontFamily: 'monospace' }}>
                                        {getMarketingRouterRuleCookieName(rule.id, variants.variant_id)}
                                      </TableCell>
                                      <TableCell>
                                        <MuiLink
                                          href={getCustomerPortalTestLink(tenant, rule, variants.variant_id)}
                                          target="_blank"
                                          rel="noreferrer"
                                          underline="hover"
                                        >
                                          test link
                                        </MuiLink>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
                {shownRules.length === 0 && !rulesQuery.isLoading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      {hasFilters && sortedRules.length > 0 ? (
                        <Typography color="text.secondary">
                          No rules match these filters.{' '}
                          <MuiLink component="button" type="button" underline="hover" onClick={resetFilters}>
                            Reset filters
                          </MuiLink>
                        </Typography>
                      ) : (
                        <Typography color="text.secondary">No marketing router rules found.</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
